/*
 * File: BuyerInput.tsx
 * Project: papa-bear
 * -----
 * Last Modified: 20 Jan 2022 4:05:07 pm
 * -----
 * Copyright (c) 2022 Elegant Software Solutions All rights reserved.
 */

import { PlusOutlined } from '@ant-design/icons';
import { Button, Divider, Form, Select, Space, Spin, Typography } from 'antd';
import { CreateSalesOrderType } from 'common/enums/CreateSalesOrderType';
import { useFormikContext } from 'formik';
import { InlineStylesModel } from 'models/InlineStylesModel';
import { SalesOrderPayloadModel } from 'models/SalesOrderPayloadModel';
import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { ReduxState } from 'redux/rootReducer';
import { useGetBuyersQuery } from 'redux/services/useBeautifylDayMonsterService';
import { AddBuyerModal } from '../AddBuyerModal';
import { BlockLoader } from '../BlockLoader';

const styles: InlineStylesModel = {
  formContainer: {
    paddingRight: 10,
    paddingLeft: 10
  },
  formItem: {
    marginBottom: 10
  },
  titleColor: {
    color: '#2F5596'
  },
  buyerButton: {
    width: '100%'
  }
};

const { Title } = Typography;

export const BuyerInput = (): JSX.Element => {
  const {
    values: { customerId, buyerId, buyerName },
    setFieldValue
  } = useFormikContext<SalesOrderPayloadModel>();
  const { selectedCompanyDataAreaId: dataAreaId } = useSelector((state: ReduxState) => state.salesOrders);
  const { pathname } = useLocation();
  const isPosted = pathname.includes('/posted');

  const [inputValue, _setInputValue] = useState<string | undefined>(undefined);
  const [offset, _setOffset] = useState(0);
  const [showBuyerInputs, _setShowBuyerInputs] = useState(false);

  /* ****************** Hook API ****************** */
  const { data: buyersResponse, isLoading, isFetching } = useGetBuyersQuery({ dataAreaId: dataAreaId ?? '', buyerNameContains: inputValue, offset, customerId });

  /* ****************** Variables ****************** */
  const buyerSearchOptions = useMemo(
    () => buyersResponse?.data?.map((buyer) => ({ label: `${buyer.buyerName} - ${buyer.buyerId}`, value: `${buyer.buyerName} - ${buyer.buyerId}` })),
    [buyersResponse]
  );

  /* ****************** Functions ****************** */
  // Handle delay of searching data
  // const handleSearch = useDebouncedCallback((value: string | undefined) => {
  //   _setOffset(0);
  //   _setInputValue(value);
  // }, 600);

  const handleClear = (): void => {
    _setInputValue(undefined);
    _setOffset(0);
    setFieldValue('buyerName' as keyof SalesOrderPayloadModel, null);
    setFieldValue('buyerId' as keyof SalesOrderPayloadModel, null);
  };

  const handleScroll = (event: React.UIEvent<HTMLDivElement>): void => {
    const target = event.currentTarget;

    if (!isLoading && !isFetching && target.scrollTop + target.offsetHeight === target.scrollHeight && buyersResponse && buyersResponse?.totalCount - offset > 25) {
      _setOffset((prevState) => prevState + 25);
    }
  };

  const handleSelect = (value: string): void => {
    setFieldValue('buyerName' as keyof SalesOrderPayloadModel, value.split(' - ')[0]);
    setFieldValue('buyerId' as keyof SalesOrderPayloadModel, value.split(' - ')[1]);
    _setInputValue(undefined);
  };

  const isInvalid = !customerId && !buyerId;

  /* ****************** JSX ****************** */
  const showCreateBuyerButtonJSX = (
    <Button icon={<PlusOutlined />} style={styles.buyerButton} onClick={(): void => _setShowBuyerInputs(true)}>
      Add New Buyer
    </Button>
  );

  const dropDownRenderJSX = (menu: JSX.Element): JSX.Element => {
    if ((isLoading || isFetching) && !offset)
      return (
        <div style={{ textAlign: 'center', padding: 10 }}>
          <Spin indicator={<BlockLoader direction="loader loader--slideUp" />} spinning />
        </div>
      );

    const moreResultsJSX =
      (isLoading || isFetching) && offset ? (
        <div style={{ padding: '0px 10px' }}>
          <Space>
            <Spin size="small" indicator={<BlockLoader direction="loader loader--slideUp" />} />
          </Space>
        </div>
      ) : null;

    return (
      <div>
        {menu}
        {moreResultsJSX}
        <Divider style={{ margin: '4px 0' }} />
        <div style={styles.formContainer}>{showCreateBuyerButtonJSX}</div>
      </div>
    );
  };

  const helperText = (): string | undefined => {
    if (isInvalid) {
      if (customerId && !buyerId) {
        return `No buyer found for customer ${customerId}`;
      } else {
        return 'Customer selection required';
      }
    }

    return undefined;
  };

  /* ****************** Render ****************** */
  return (
    <>
      <AddBuyerModal show={showBuyerInputs} onClose={(): void => _setShowBuyerInputs(false)} />
      <Title level={5} style={styles.titleColor}>
        {CreateSalesOrderType.BUYER}
      </Title>
      <Form.Item style={styles.formItem} validateStatus={isInvalid ? 'error' : undefined} help={helperText()}>
        <Select
          allowClear
          value={buyerName && buyerId ? `${buyerName} - ${buyerId}` : undefined}
          disabled={isInvalid || isPosted}
          dropdownRender={dropDownRenderJSX}
          // filterOption={false}
          filterOption={(input, option): boolean => (option?.value as unknown as string).toLowerCase().includes(input.toLowerCase())}
          loading={isLoading || isFetching}
          onClear={handleClear}
          onPopupScroll={handleScroll}
          // onSearch={handleSearch}
          onSelect={handleSelect}
          options={buyerSearchOptions}
          showSearch
        />
      </Form.Item>
    </>
  );
};
